import { useState, type ReactNode } from "react";
import { Lock } from "lucide-react";

import {
  useGetV1Series,
  useGetV1SitesIdSensors,
  type EnvelopeSensorViewDataSeriesRefsItem,
  type EnvelopeSensorViewDataTilesItem,
} from "@solarkey/api-client";
import { EmptyState, ErrorState, Metric, Skeleton, StateWord, formatAge } from "@solarkey/ui";
import { TimeSeriesTile } from "@solarkey/charts";

import { PageHeader } from "../shell/PageHeader";
import { SiteTabBar } from "../components/SiteTabBar";
import { useEntitlement } from "../session/capabilities";

/* ── Screen 12 (P10): Site: Weather and forecast ──
   Two halves with very different standing, and the screen says which is which.

   OBSERVED is real: GET /v1/sites/{id}/sensors?preset=weather returns the site's
   measured weather signals (irradiance, plane-of-array irradiance, module and
   ambient temperature, wind speed) and GET /v1/series serves each curve.

   FORECAST has no source at all. There is no weather or forecast endpoint in the
   frozen contract; the `forecast` table is daily-grain, holds no rows, and has no
   irradiance, temperature, wind or cloud column. So the forecast tiles, the 72-hour
   bands, forecast skill, the cleaning window and the plan comparison are WITHHELD,
   and the panels render locked — which is also what the wireframe asks for: the
   measured tiles at Easy M, the forecast panels locked until Easy T.

   This screen previously generated all of it with Math.sin() from a fixture whose
   x-axis was frozen at 6 Oct 2026, under a site name no real site has. */

/** The forecast facts a provider feed would supply, and why each is withheld. */
const WITHHELD_FORECAST: { label: string; reason: string }[] = [
  {
    label: "Irradiance, generation and plan for tomorrow",
    reason: "no forecast provider is connected and no forecast endpoint exists in the contract",
  },
  {
    label: "72-hour bands — irradiance, temperature, wind, cloud, rain",
    reason: "the forecast table is daily-grain with no weather columns, and holds no rows",
  },
  {
    label: "Forecast skill over 30 days",
    reason: "measuring skill needs stored past forecasts to compare against; none are stored",
  },
  {
    label: "Cleaning window",
    reason: "needs a rain and wind forecast plus a soiling verdict; neither is available",
  },
  {
    label: "Plan comparison for the next 7 days",
    reason: "no budget or plan table is loaded for this site",
  },
];

const twoColStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
  gap: "var(--sk-density-pad)",
  alignItems: "start",
};

const SIGNAL_LABELS: Record<string, string> = {
  irradiance: "Irradiance · GHI",
  irradiance_plane: "Irradiance · plane of array",
  irradiance_poa: "Irradiance · POA",
  irradiance_tilt: "Irradiance · tilted",
  module_temperature: "Module temperature",
  ambient_temperature: "Ambient temperature",
  wind_speed: "Wind speed",
  wind_direction: "Wind direction",
  rain_gauge: "Rain gauge",
};

function signalLabel(signal: string): string {
  return (
    SIGNAL_LABELS[signal] ??
    signal.replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase())
  );
}

export function SiteWeatherForecast({ site }: { site: string }): ReactNode {
  // Default to the half that has real data behind it. The screen used to open on
  // the forecast, so the first thing anyone saw was the synthetic half.
  const [view, setView] = useState<"observed" | "forecast">("observed");
  const forecastUnlocked = useEntitlement("weather_forecast");
  const query = useGetV1SitesIdSensors(site, { preset: "weather" });

  const header = (
    <>
      <PageHeader
        title={`Site ${site} · Weather and forecast`}
        scope="Measured on site · the 72-hour forecast is not sourced yet"
        timeControl="none"
        action={
          <div className="sk-scenario" role="group" aria-label="View">
            <button
              type="button"
              data-active={view === "observed" ? "true" : "false"}
              onClick={() => setView("observed")}
            >
              Observed
            </button>
            <button
              type="button"
              data-active={view === "forecast" ? "true" : "false"}
              onClick={() => setView("forecast")}
              aria-describedby="wx-forecast-locked"
            >
              Forecast
              {forecastUnlocked ? null : (
                <Lock size={11} aria-hidden style={{ marginLeft: 4, verticalAlign: "-1px" }} />
              )}
            </button>
          </div>
        }
      />
      <SiteTabBar site={site} />
    </>
  );

  if (view === "forecast") {
    return (
      <div className="flex flex-col gap-[14px]">
        {header}
        <ForecastLocked unlocked={forecastUnlocked} />
      </div>
    );
  }

  if (query.isLoading) {
    return (
      <div className="flex flex-col gap-[14px]" aria-busy="true">
        {header}
        <div className="sk-sensor-grid">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} variant="tile" />
          ))}
        </div>
      </div>
    );
  }

  if (query.isError || !query.data || query.data.status !== 200) {
    return (
      <div className="flex flex-col gap-[14px]">
        {header}
        <ErrorState
          title="Measured weather unavailable"
          message="The site's weather signals could not be read. Nothing is drawn rather than a modelled curve."
          onRetry={() => void query.refetch()}
        />
      </div>
    );
  }

  const d = query.data.data.data;
  const tiles = (d.tiles ?? []).filter((t) => t.signal);
  const seriesRefs = (d.series_refs ?? []).filter((r) => r.signal && r.from && r.to);

  return (
    <div className="flex flex-col gap-[14px]">
      {header}

      <section aria-labelledby="wx-obs-tiles">
        <h2 id="wx-obs-tiles" className="sr-only">Measured weather now</h2>
        {tiles.length > 0 ? (
          <div className="sk-tiles-5">
            {tiles.map((tile, i) => (
              <ObservedTile key={tile.device_id ? `${tile.device_id}-${tile.signal}` : i} tile={tile} />
            ))}
          </div>
        ) : (
          <EmptyState
            variant="no_data"
            title="No weather sensor is mapped on this site"
            message="The weather preset returned no signal. A missing sensor counts against coverage; it is never reported as zero."
          />
        )}
      </section>

      {tiles.length > 0 ? (
        <section aria-labelledby="wx-obs-curves" className="sk-section">
          <div className="sk-section__head">
            <div>
              <h2 id="wx-obs-curves" className="sk-section__title">Measured on site</h2>
              <span className="sk-section__sub">
                as acquired · no forecast overlay, because none is sourced
              </span>
            </div>
          </div>
          <div className="sk-sensor-grid">
            {tiles.map((tile, i) => {
              // device + signal, so a tile never borrows another device's curve.
              const ref = seriesRefs.find(
                (r) =>
                  r.signal === tile.signal &&
                  (tile.device_id == null || r.device_id === tile.device_id),
              );
              return ref ? (
                <ObservedChart
                  key={tile.device_id ? `${tile.device_id}-${tile.signal}` : i}
                  tile={tile}
                  seriesRef={ref}
                />
              ) : null;
            })}
          </div>
        </section>
      ) : null}

      {/* Two columns, not the three `.sk-wx-cols` reserved for the retired
          plan-comparison panel — auto-fit so it stacks on a narrow viewport
          without touching the stylesheet. */}
      <div style={twoColStyle}>
        <section aria-labelledby="wx-src" className="sk-section">
          <div className="sk-section__head">
            <div>
              <h2 id="wx-src" className="sk-section__title">Sources</h2>
              <span className="sk-section__sub">provenance and freshness per signal, as reported</span>
            </div>
          </div>
          <table className="sk-src-table">
            <thead>
              <tr><th>Quantity</th><th>Source</th><th>Fresh</th></tr>
            </thead>
            <tbody>
              {tiles.map((tile, i) => (
                <tr key={tile.device_id ? `${tile.device_id}-${tile.signal}` : i}>
                  <td>{signalLabel(tile.signal!)}</td>
                  <td>
                    {tile.value_now?.state === "NOT_MEASURED" ? (
                      <span className="sk-stateword-sm">Not measured</span>
                    ) : (
                      <>
                        {tile.device_id ?? "site sensor"}
                        {tile.value_now?.provenance ? (
                          <> <span className="sk-basis-chip">{tile.value_now.provenance}</span></>
                        ) : null}
                      </>
                    )}
                  </td>
                  <td className="sk-src-table__fresh">{freshnessText(tile)}</td>
                </tr>
              ))}
              <tr>
                <td>Forecast, cloud, rain</td>
                <td><span className="sk-stateword-sm">Not measured</span></td>
                <td className="sk-src-table__fresh">no provider connected</td>
              </tr>
            </tbody>
          </table>
        </section>

        <ForecastLocked unlocked={forecastUnlocked} compact />
      </div>
    </div>
  );
}

/** Freshness as reported by the API — never a made-up "12 s". */
function freshnessText(tile: EnvelopeSensorViewDataTilesItem): string {
  const f = tile.value_now?.freshness;
  if (f?.age_s !== undefined && f.age_s !== null) return formatAge(f.age_s);
  if (f?.last_at) return String(f.last_at).slice(11, 16);
  return "not reported";
}

function ObservedTile({ tile }: { tile: EnvelopeSensorViewDataTilesItem }): ReactNode {
  const withheld =
    !tile.value_now ||
    tile.value_now.value === null ||
    tile.value_now.value === undefined;

  return (
    <div className="sk-tile">
      <span className="sk-tile__label">{signalLabel(tile.signal!)}</span>
      <span className="sk-metric__value">
        {withheld ? (
          <StateWord state={tile.value_now?.state ?? "CANNOT_CHECK"} />
        ) : (
          <Metric figure={tile.value_now!} showBasis={false} showFreshness={false} showProvenance={false} />
        )}
      </span>
      <span className="sk-tile__sub">
        {tile.value_now?.provenance ? (
          <span className="sk-basis-chip">{tile.value_now.provenance}</span>
        ) : null}{" "}
        {withheld ? "no accepted reading" : `fresh ${freshnessText(tile)}`}
      </span>
    </div>
  );
}

function ObservedChart({
  tile,
  seriesRef,
}: {
  tile: EnvelopeSensorViewDataTilesItem;
  seriesRef: EnvelopeSensorViewDataSeriesRefsItem;
}): ReactNode {
  const label = signalLabel(tile.signal!);
  const query = useGetV1Series({
    site: seriesRef.site ?? "",
    device: seriesRef.device,
    signal: seriesRef.signal ?? "",
    from: seriesRef.from ?? "",
    to: seriesRef.to ?? "",
    grain: seriesRef.grain,
  });

  const shell = (body: ReactNode): ReactNode => (
    <div className="sk-sm-tile">
      <div className="sk-sm-header">
        <div className="sk-sm-header__top">
          <span className="sk-sm-header__label">{label}</span>
        </div>
      </div>
      {body}
    </div>
  );

  if (query.isLoading) return shell(<Skeleton variant="tile" />);
  if (query.isError || !query.data || query.data.status !== 200) {
    return shell(<div className="sk-sm-tile__empty">series unavailable</div>);
  }

  const s = query.data.data.data;
  const points = s.points ?? [];
  if (points.length === 0) return shell(<div className="sk-sm-tile__empty">no accepted points</div>);

  return (
    <TimeSeriesTile
      label={label}
      unit={tile.value_now?.unit ?? s.unit ?? ""}
      times={points.map((p) => Date.parse(p.ts) / 1000)}
      values={points.map((p) => p.value ?? null)}
      height={120}
    />
  );
}

/** The forecast half, stated as the gap it is. No tiles, no bands, no numbers. */
function ForecastLocked({ unlocked, compact }: { unlocked: boolean; compact?: boolean }): ReactNode {
  return (
    <section aria-labelledby="wx-forecast-locked" className="sk-section">
      <div className="sk-section__head">
        <div>
          <h2 id="wx-forecast-locked" className="sk-section__title">
            <Lock size={13} aria-hidden style={{ marginRight: 6, verticalAlign: "-2px" }} />
            72-hour forecast
          </h2>
          <span className="sk-section__sub">
            {unlocked
              ? "entitled, but not sourced — no forecast provider is connected"
              : "locked · unlocks at Easy T, and needs a forecast provider"}
          </span>
        </div>
      </div>
      <p className="sk-set-note">
        Nothing forward-looking is shown on this screen. There is no weather or
        forecast endpoint in the frozen contract, and the forecast table is
        daily-grain with no weather columns and no rows. A band drawn from a model
        would read as a measurement, so none is drawn.
      </p>
      {compact ? null : (
        <table className="sk-set-table" aria-label="Withheld forecast figures">
          <thead>
            <tr><th>Withheld</th><th>Reason</th></tr>
          </thead>
          <tbody>
            {WITHHELD_FORECAST.map((row) => (
              <tr key={row.label}>
                <td>{row.label}</td>
                <td className="sk-set-from">{row.reason}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}
